import { logger } from "@/common/logger";
import { ActionContext } from "@/components/DatasetVersionHistory/common";
import { Edit02Icon, Edit03Icon } from "@/icons";
import { Button } from "@/kit/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/kit/ui/form";
import { Input } from "@/kit/ui/input";
import {
  ResponsiveModal,
  ResponsiveModalClose,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
} from "@/kit/ui/responsive-modal";
import { preprocessEdit, relayErrorMessage } from "@/utils/relay";
import { toast } from "@/utils/toast";
import { useValibotForm } from "@/utils/validation";
import { useContext, useState } from "react";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";
import { graphql, useMutation } from "react-relay";
import * as v from "valibot";
import { EditActionVersionHistoryMutation as EditActionVersionHistoryMutationType } from "./__generated__/EditActionVersionHistoryMutation.graphql";
import { semverSchema } from "@/utils/schema";

const schema = (intl: IntlShape) =>
  v.object({
    version: semverSchema(intl),
  });

const EditActionVersionHistoryMutation = graphql`
  mutation EditActionVersionHistoryMutation(
    $id: ID!
    $input: UpdateDatasetVersionInput!
  ) {
    updateDatasetVersion(datasetVersionId: $id, input: $input) {
      version
    }
  }
`;

export function EditAction() {
  const { version, id } = useContext(ActionContext);
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [commitMutation, isMutationInFlight] =
    useMutation<EditActionVersionHistoryMutationType>(
      EditActionVersionHistoryMutation,
    );
  const form = useValibotForm(schema(intl), {
    mode: "onChange",
    defaultValues: { version },
  });

  const doSubmit = form.handleSubmit((data) => {
    commitMutation({
      variables: {
        id,
        input: preprocessEdit(data, { version }),
      },
      onCompleted(_) {
        toast.success(
          <FormattedMessage defaultMessage="Dataset version renamed" />,
        );
      },
      onError(error) {
        logger.error(error);
        toast.error(relayErrorMessage(error));
      },
    });
    setOpen(false);
  });

  return (
    <ResponsiveModal open={open} onOpenChange={setOpen}>
      <ResponsiveModalTrigger asChild>
        <Button
          variant="tertiary"
          hue="grey"
          size="md"
          className="flex flex-row items-center justify-start gap-2 w-full hover:bg-linear-to-r from-[#F2F4F7] to-[#FFF] rounded-none bg-none px-3 py-4"
        >
          <Edit02Icon className="w-6 h-6" />
          <FormattedMessage defaultMessage="Rename version" />
        </Button>
      </ResponsiveModalTrigger>

      <ResponsiveModalContent className="sm:w-96 w-auto bg-linear-to-b from-[#EFEDFC] from-[-16.39%] to-white to-[65.62%] ">
        <Form form={form} onSubmit={doSubmit} className="w-full">
          <div className="flex flex-col items-center justify-center gap-3">
            <div className="bg-brand-50 rounded-md p-2 w-fit">
              <Edit03Icon className="text-brand-500 w-8 h-8" />
            </div>
            <ResponsiveModalTitle>
              <FormattedMessage defaultMessage="Rename version" />
            </ResponsiveModalTitle>
            <ResponsiveModalDescription className="text-center w-10/12">
              <FormattedMessage
                defaultMessage="You're about to delete “{version}” and all its data. This action can’t be undone."
                values={{ version }}
              />
            </ResponsiveModalDescription>

            <FormField
              control={form.control}
              name="version"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>
                    <FormattedMessage defaultMessage="Version name" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={intl.formatMessage({
                        defaultMessage: "Enter a unique SemVer name",
                      })}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="w-full flex flex-row gap-2 mt-4">
              <ResponsiveModalClose asChild>
                <Button
                  variant="secondary"
                  disabled={isMutationInFlight}
                  className="w-full"
                >
                  <FormattedMessage defaultMessage="Cancel" />
                </Button>
              </ResponsiveModalClose>
              <Button
                type="submit"
                disabled={isMutationInFlight || !form.formState.isDirty}
                className="w-full"
              >
                <FormattedMessage defaultMessage="Save" />
              </Button>
            </div>
          </div>
        </Form>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
